import React, { useEffect, useState } from "react";
import { Sparkles, X, Gift, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { festivalAccent } from "../lib/festivalThemes";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const FestivalBanner = ({ festival }) => {
  const { user, refreshWallet } = useAuth();
  const dismissKey = festival ? `rl_festival_dismissed_${festival.name}` : "";
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });
  const [bonus, setBonus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !festival) return;
    api.get("/festival-bonus/status").then((r) => setBonus(r.data)).catch(() => {});
  }, [user, festival?.name]); // eslint-disable-line

  const claimable = !!(bonus?.available && bonus.mode !== "auto");
  if (!festival || !festival.greeting || (hidden && !claimable)) return null;
  const a = festivalAccent(festival.theme);

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
    setHidden(true);
  };

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/festival-bonus/claim");
      toast.success(`🎁 ₹${data.amount} ${data.festival} bonus added to your wallet!`);
      refreshWallet();
      setBonus((b) => ({ ...b, available: false, claimed: true }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBonus((b) => ({ ...b, available: false, claimed: true }));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden px-4 py-3 rl-fade-up"
      data-testid="festival-banner"
      style={{ background: a.bg, color: a.text, boxShadow: `0 8px 26px -8px ${a.glow}` }}
    >
      <div className="rl-festival-shine" />
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: a.chip }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 relative z-10">
          <div className="font-display font-black text-base leading-tight" data-testid="festival-banner-greeting">{festival.greeting}</div>
          <div className="text-[11px] opacity-85">Celebrate {festival.name} with ROYAL LAKSHMI — play & win big today!</div>
        </div>
        {!claimable && <button onClick={dismiss} className="relative z-10 opacity-70 hover:opacity-100 shrink-0" data-testid="festival-banner-close" aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>}
      </div>
      {claimable && (
        <button onClick={claim} disabled={busy} className="relative z-10 mt-2.5 w-full rounded-xl py-2.5 text-sm font-black flex items-center justify-center gap-2 bg-white text-slate-900 shadow-lg active:scale-[0.98] transition-transform disabled:opacity-60" data-testid="festival-bonus-claim">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}Claim ₹{bonus.amount} {festival.name} bonus · பண்டிகை bonus-ஐ பெறுங்க
        </button>
      )}
      {bonus?.claimed && (
        <div className="relative z-10 mt-2 text-[11px] font-bold flex items-center gap-1.5 opacity-90" data-testid="festival-bonus-claimed"><CheckCircle2 className="w-3.5 h-3.5" />₹{bonus.amount} {festival.name} bonus {bonus.mode === "auto" ? "credited to your wallet" : "claimed"} — good luck today!</div>
      )}
    </div>
  );
};
